import { useState, useEffect, ChangeEvent, FormEvent, MouseEvent } from "react";
import FileUpload from "../FileUpload";
import { useAuth0 } from "@auth0/auth0-react";
import SVGComponent from "./SVGComponent";
import axios from "axios";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

// --- INTERFACES ---
interface SubmissionContent {
  description: boolean;
  picture: boolean;
  url: boolean;
}

interface SubmissionProgress {
  inReview: boolean;
  approved: boolean;
  denied: boolean;
  completed: boolean;
}

interface SubmissionInterface {
  userID: string;
  username: string;
  pfp: string;
  picture?: string;
  description?: string;
  url?: string;
  progress: SubmissionProgress;
}

interface SubmissionDoc {
  _id: string;
  postID: string;
  posterAuth0ID: string;
  structure: SubmissionContent;
  userSubmissions: SubmissionInterface[];
}

interface SelectedProps {
  isSelected: boolean;
  posterID: string;
  currentUserID: string;
  isActive: boolean;
  postID: string;
  SubmissionIDPair: string;
  submissionContent: SubmissionContent;
}

// --- COMPONENT ---
export default function Submission(props: SelectedProps) {
  const { getAccessTokenSilently } = useAuth0();

  // --- STATE ---
  const [, setPaidFor] = useState(false);
  const [submissionDocs, setSubmissionDocs] = useState<SubmissionDoc[]>([]);
  const [userSubmissionData, setUserSubmissionData] = useState({
    description: "",
    picture: "",
    url: "",
  });
  const [postActive, setPostActive] = useState(false);
  const [submissionSettings, setSubmissionSettings] = useState({
    picture: false,
    description: false,
    url: false,
  });
  const [message, setMessage] = useState("");
  const [collapsedItems, setCollapsedItems] = useState<string[]>([]);

  // --- EFFECTS ---
  // Fetch submissions data (for poster view)
  useEffect(() => {
    if (props.isActive && props.posterID === props.currentUserID) {
      const fetchData = async () => {
        try {
          const accessToken = await getAccessTokenSilently();
          const response = await axios.get<SubmissionDoc[]>(
            `${backend_domain}${backend_port}/user_submissions/pull-submissions/${props.postID}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          setSubmissionDocs(response.data);
        } catch (error) {
          console.error("Error fetching submissions:", error);
        }
      };
      fetchData();
    }
  }, [props.isActive, props.postID, props.posterID, props.currentUserID, getAccessTokenSilently]);

  // Fetch user's access and submission data (for user view)
  useEffect(() => {
    if (props.isActive && props.posterID !== props.currentUserID && props.SubmissionIDPair) {
      const fetchData = async () => {
        try {
          const accessToken = await getAccessTokenSilently();
          const response = await axios.get(
            `${backend_domain}${backend_port}/user_submissions/user-access-to-submission/${props.SubmissionIDPair}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          setPaidFor(response.data.paidFor);
          if (response.data.UserSubmission) {
            setUserSubmissionData({
              description: response.data.UserSubmission.description || "",
              picture: response.data.UserSubmission.picture || "",
              url: response.data.UserSubmission.url || "",
            });
          }
        } catch (error) {
          console.error("Error fetching user access data:", error);
        }
      };
      fetchData();
    }
  }, [props.isActive, props.SubmissionIDPair, props.posterID, props.currentUserID, getAccessTokenSilently]);

  // --- HANDLERS ---
  const toggleCollapse = (userID: string) => {
    setCollapsedItems((prev) =>
      prev.includes(userID)
        ? prev.filter((id) => id !== userID)
        : [...prev, userID]
    );
  };

  const handleOpenSubmissions = async (e: FormEvent) => {
    e.preventDefault();
    setMessage("");
    try {
      const accessToken = await getAccessTokenSilently();
      await axios.post(
        `${backend_domain}${backend_port}/user_submissions/open-submissions/${props.postID}`,
        submissionSettings,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setMessage("Submissions opened successfully! Please refresh the page to see changes.");
    } catch (err) {
      console.log(err);
      setMessage("An error occurred while opening submissions.");
    }
  };

  const handleSubmissionProgressUpdate = async (e: MouseEvent) => {
    e.preventDefault();
    setMessage("");
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await axios.post<SubmissionDoc>(
        `${backend_domain}${backend_port}/user_submissions/update-submission-status/${props.SubmissionIDPair}`,
        { SubmissionProgressArray: submissionDocs },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setSubmissionDocs(prevDocs => prevDocs.map(doc => doc._id === response.data._id ? response.data : doc));
      setMessage("Progress updated successfully!");
    } catch (err) {
      console.log(err);
      setMessage("An error occurred while updating progress.");
    }
  };

  const handleUserSubmissionSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage("");
    try {
      const accessToken = await getAccessTokenSilently();
      await axios.post(
        `${backend_domain}${backend_port}/user_submissions/post-user-submission/${props.SubmissionIDPair}`,
        userSubmissionData,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setMessage("Submission uploaded successfully!");
    } catch (err) {
      console.log(err);
      setMessage("An error occurred while uploading your submission.");
    }
  };

  const handleRadioChange = (userID: string, option: keyof SubmissionProgress) => {
    setSubmissionDocs(prevDocs => {
      if (prevDocs.length === 0) return [];
      const newDocs = [...prevDocs];
      const userSubmissions = newDocs[0].userSubmissions.map(sub =>
        sub.userID === userID
          ? {
              ...sub,
              progress: {
                inReview: option === "inReview",
                approved: option === "approved",
                denied: option === "denied",
                completed: option === "completed",
              },
            }
          : sub
      );
      newDocs[0] = { ...newDocs[0], userSubmissions };
      return newDocs;
    });
  };

  const handleSettingsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setSubmissionSettings(prev => ({ ...prev, [name]: checked }));
  };

  const handleUserSubmissionChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUserSubmissionData(prev => ({ ...prev, [name]: value }));
  };

  const chooseImgUrl = (url: string) => {
    setUserSubmissionData(prev => ({ ...prev, picture: url }));
  };

  // --- RENDER LOGIC ---
  if (!props.isSelected) {
    return null;
  }

  // --- RENDER: POSTER VIEW ---
  if (props.posterID === props.currentUserID) {
    if (!props.isActive) {
      // Poster view, submissions NOT active
      return (
        <>
          {!postActive ? (
            <div className="text-black container mx-auto flex flex-col items-center justify-center p-3 space-y-8 md:p-10 md:px-24 xl:px-48">
              <SVGComponent />
              <h1 className="text-black font-bold text-3xl leading-none text-center">Nothing here yet</h1>
              <button onClick={() => setPostActive(true)} className="text-2xl bg-black md:px-32 px-20 py-2 text-white border">
                Open Submissions
              </button>
            </div>
          ) : (
            <section className="p-6 dark:bg-gray-800 dark:text-gray-50">
              <form onSubmit={handleOpenSubmissions}>
                <fieldset className="mx-auto grid grid-cols-4 gap-6 p-6 rounded-md">
                  <div className="text-4xl font-bold col-span-full">
                    Submission Settings
                  </div>
                </fieldset>
                {(['picture', 'description', 'url'] as const).map(type => (
                  <fieldset key={type} className="mx-auto grid grid-cols-4 gap-6 px-6 pb-6 rounded-md shadow-sm dark:bg-gray-900">
                    <div className="col-span-full sm:col-span-full border rounded-lg border-gray-300">
                      <div className="flex justify-between p-2 items-center">
                        <div>Allow {type.charAt(0).toUpperCase() + type.slice(1)} Submissions</div>
                        <input
                          name={type}
                          onChange={handleSettingsChange}
                          checked={submissionSettings[type]}
                          className="mr-2 mt-1 h-3.5 w-8 appearance-none rounded-full bg-neutral-300 ... checked:bg-primary-500"
                          type="checkbox"
                          role="switch"
                        />
                      </div>
                    </div>
                  </fieldset>
                ))}
                <button type="submit" className="px-16 py-3 font-semibold rounded-xl bg-primary-300">
                  Publish
                </button>
              </form>
              {message && <p className="text-center mt-2">{message}</p>}
            </section>
          )}
        </>
      );
    } else {
      // Poster view, submissions ARE active
      return (
        <div>
          <div className="flex justify-between items-center mb-2">
            <div className="w-1/2 font-semibold">Current Submissions:</div>
            <div className="flex flex-row w-1/2 text-center font-semibold">
              <div className="w-1/4">In-Review</div>
              <div className="w-1/4">Approved</div>
              <div className="w-1/4">Denied</div>
              <div className="w-1/4">Completed</div>
            </div>
            <span className="w-8"></span>
          </div>
          {(submissionDocs[0]?.userSubmissions || []).map((submission) => (
            <div key={submission.userID} className="border p-2 mb-2 rounded-lg">
              <div className="font-semibold flex justify-between items-center">
                <div className="flex flex-row items-center w-1/2">
                  <img src={submission.pfp} className="h-8 w-8 rounded-full" alt={submission.username} />
                  <div className="block px-2 truncate">{submission.username}</div>
                </div>
                <div className="flex flex-row justify-around w-1/2">
                  {(['inReview', 'approved', 'denied', 'completed'] as const).map(status => (
                    <div key={status} className="w-1/4 text-center">
                      <input
                        type="radio"
                        name={`radio-${submission.userID}`}
                        className="radio radio-primary"
                        checked={submission.progress[status]}
                        onChange={() => handleRadioChange(submission.userID, status)}
                      />
                    </div>
                  ))}
                </div>
                <span
                  className="border border-black rounded-xl px-2 w-8 text-center cursor-pointer"
                  onClick={() => toggleCollapse(submission.userID)}
                >
                  {collapsedItems.includes(submission.userID) ? "-" : "+"}
                </span>
              </div>
              {collapsedItems.includes(submission.userID) && (
                <div className="pt-2 mt-2 border-t flex md:flex-row flex-col justify-evenly gap-2">
                  {submission.picture && (
                    <div className="border border-gray-300 rounded-xl md:w-1/3 p-2">
                      <span className="font-semibold">Picture:</span>
                      <img src={submission.picture} alt="Submission" className="rounded-lg mt-1 max-w-full h-auto" />
                    </div>
                  )}
                  {submission.description && (
                    <div className="border border-gray-300 rounded-xl md:w-1/3 p-2">
                      <span className="font-semibold">Text:</span> {submission.description}
                    </div>
                  )}
                  {submission.url && (
                    <div className="border border-gray-300 rounded-xl md:w-1/3 p-2">
                      <span className="font-semibold">URL:</span> <a href={submission.url} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 break-all">{submission.url}</a>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="col-span-full mt-4">
            <button
              className="w-full text-white font-bold bg-primary-500 border rounded-xl text-xl py-4 text-center hover:bg-primary-600"
              onClick={handleSubmissionProgressUpdate}
            >
              Save Changes
            </button>
          </div>
          {message && <p className="text-center mt-2 text-green-600">{message}</p>}
        </div>
      );
    }
  }

  // --- RENDER: USER VIEW ---
  if (props.posterID !== props.currentUserID) {
    if (!props.isActive) {
      return (
        <div className="text-black container mx-auto flex flex-col items-center justify-center p-3 space-y-8 md:p-10 md:px-24 xl:px-48">
          <SVGComponent />
          <h1 className="text-black font-bold text-3xl leading-none text-center">
            Submissions are currently not enabled.
          </h1>
        </div>
      );
    }

    return (
      <>
        <div className="text-xl text-center font-bold">Your Submission</div>
        <form onSubmit={handleUserSubmissionSubmit} className="mt-4">
          <div className="px-6 py-4 grid grid-cols-1 gap-4 col-span-full border rounded-xl border-gray-200">
            {props.submissionContent.description && (
              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  name="description"
                  value={userSubmissionData.description}
                  onChange={handleUserSubmissionChange}
                  placeholder="Your submission text..."
                  required
                  className="mt-1 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
            {props.submissionContent.url && (
              <div>
                <label className="text-sm font-medium">Link</label>
                <input
                  name="url"
                  value={userSubmissionData.url}
                  onChange={handleUserSubmissionChange}
                  type="url"
                  placeholder="https://example.com"
                  required
                  className="mt-1 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
            {props.submissionContent.picture && (
              <div>
                <label className="text-sm font-medium">Picture</label>
                {userSubmissionData.picture && <img src={userSubmissionData.picture} className="h-64 w-64 my-2 rounded-lg object-cover" alt="Your submission" />}
                <FileUpload chooseImgUrl={chooseImgUrl} />
              </div>
            )}
            <div>
              <div className="text-center">
                <button type="submit" className="px-16 py-3 font-semibold rounded-xl bg-primary-300 hover:bg-primary-400">
                  Submit
                </button>
              </div>
            </div>
          </div>
          {message && <p className="text-center mt-2 text-green-600">{message}</p>}
        </form>
      </>
    );
  }

  return null;
}
